// The date math for per-brand schedules.
//
// A brand's schedule is eight cells on a fixed 4-week calendar; every real date
// derives from a single anchor (the `release` milestone) and one post date per
// batch. This file is that derivation, pure and testable away from any store.
//
// EVERYTHING IS UTC (+0), ALWAYS. A schedule date is a CALENDAR date with no
// time — "the Jul 27 release" — so we compute and format it at +0 and every
// viewer, in any timezone, sees the same day of the week. A bare ISO date handed
// to `new Date(iso)` parses as UTC midnight and renders the day before west of
// Greenwich; we never do that.

// SUN = 0 … SAT = 6 — weeks start on Sunday, as every calendar in this app draws
// them. This is `Date.getUTCDay()`'s own numbering, so `dowOf` is now a straight
// read with no shift, and the one place a shift used to live cannot go stale.
//
// cell.week is a TIMELINE week (0..3) — the week WITHIN one batch's lifecycle —
// never a batch/Videos-page week number. A week runs Sun→Sat, so `gridDay`
// (week * 7 + dow) puts Sunday at the START of its week: a milestone on Wk2-Sun
// falls the day AFTER Wk1-Sat, which is what the grid draws.
export type Cell = { week: 0 | 1 | 2 | 3; dow: 0 | 1 | 2 | 3 | 4 | 5 | 6 };

export type MilestoneKey =
  | "start-writing"
  | "scripts-due"
  | "script-am-review-due"
  | "script-brand-review-due"
  | "footage-upload-due"
  | "footage-am-review-due"
  | "footage-brand-review-due"
  | "release"; // the anchor: its dow constrains the post date; its offset is the frame

export type BrandSchedule = Record<MilestoneKey, Cell>; // all 8 keys always present

// The order the config page lists them and the calendar draws them.
export const MILESTONE_ORDER: MilestoneKey[] = [
  "start-writing",
  "scripts-due",
  "script-am-review-due",
  "script-brand-review-due",
  "footage-upload-due",
  "footage-am-review-due",
  "footage-brand-review-due",
  "release",
];

// The default cells. Milestones 6 and 7 share Wk2-Fri
// on purpose (the brief: "AM reviews and client reviews are the next day
// Friday") — the config UI must let two boxes sit on one day.
//
// dow is SUN=0, so Mon=1 … Fri=5. The named cells (Wk1-Mon, Wk3-Mon, …) are
// the original ones; only the integer that spells them moved.
export const DEFAULT_SCHEDULE: BrandSchedule = {
  "start-writing": { week: 0, dow: 1 }, // Wk1-Mon
  "scripts-due": { week: 0, dow: 2 }, // Wk1-Tue
  "script-am-review-due": { week: 0, dow: 4 }, // Wk1-Thu
  "script-brand-review-due": { week: 0, dow: 5 }, // Wk1-Fri
  "footage-upload-due": { week: 1, dow: 4 }, // Wk2-Thu
  "footage-am-review-due": { week: 1, dow: 5 }, // Wk2-Fri
  "footage-brand-review-due": { week: 1, dow: 5 }, // Wk2-Fri
  release: { week: 2, dow: 1 }, // Wk3-Mon
};

/** A cell flattened to a day index on the 4-week grid: 0 (Wk1-Mon) .. 27 (Wk4-Sun). */
export const gridDay = (c: Cell): number => c.week * 7 + c.dow;

/** The day-of-week (Mon=0) the post date must land on — the anchor's own dow. */
export const allowedPostDow = (s: BrandSchedule): number => s.release.dow;

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]; // Sun=0

/** Add whole days to a "YYYY-MM-DD" in UTC; returns "YYYY-MM-DD". Input unchanged if unparseable. */
export function addDaysUtc(iso: string, days: number): string {
  const m = ISO.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  const dt = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)) + days * 86_400_000);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${dt.getUTCFullYear()}-${p(dt.getUTCMonth() + 1)}-${p(dt.getUTCDate())}`;
}

/**
 * Whole days from `from` to `to`, negative when `to` is the earlier one.
 *
 * UTC MIDNIGHT TO UTC MIDNIGHT, like every other date in this file: these are
 * wall-calendar days, not instants, so there is no hour to round and no DST
 * boundary to fall over. Two dates that are the same day are zero days apart.
 *
 * It is what turns "late" into "late 3d" — a verdict into a fact — on the
 * throughput page's evidence popup.
 * A string either side that is not an ISO date answers 0, the same
 * refuse-to-invent that `addDaysUtc` makes above.
 */
export function daysBetweenUtc(from: string, to: string): number {
  const a = ISO.exec(from);
  const b = ISO.exec(to);
  if (!a || !b) return 0;
  const ms = (m: RegExpExecArray) => Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Math.round((ms(b) - ms(a)) / 86_400_000);
}

/**
 * A milestone's real calendar date for a batch whose post date is `postDate`:
 * postDate shifted by the difference of grid offsets. The only
 * stored date is the anchor; reconfigure the schedule and every date moves.
 */
export function milestoneDate(s: BrandSchedule, key: MilestoneKey, postDate: string): string {
  return addDaysUtc(postDate, gridDay(s[key]) - gridDay(s.release));
}

// ─── The week's publish days ─────────────────────────────────────────────────
// A batch's five videos go out on five consecutive
// BUSINESS days, starting on the post date itself: video 1 on the post date,
// video 2 the next working day, and so on. A Wednesday post date therefore lands
// its week on Wed, Thu, Fri, Mon, Tue — the weekend is stepped over, not counted.
//
// Nothing here validates that the post date is itself a weekday. It always is:
// allowedPostDow constrains it to the brand's release dow, and no brand releases
// on a Sunday. If one ever did, day 1 would be that Sunday and days 2-5 would be
// the following Mon-Thu, which is the only sane reading of "and four more".

/**
 * The publish days a week has when nothing says otherwise — and THE ONE FIVE IN
 * THE APP, since lib/commitment.ts's default is this constant rather than a
 * second literal. Exported for that reason alone: the audit's "no invented
 * numbers" rule holds for five only because five is the shape of a real week
 * here, and that argument needs there to be one of it.
 */
export const VIDEOS_PER_WEEK = 5;

/** Is this a Saturday or Sunday? The publish calendar steps over both. */
const isWeekend = (iso: string): boolean => {
  const d = dowOf(iso);
  return d === 0 || d === 6; // SUN | SAT
};

/**
 * The publish dates of a batch's videos, day 1 first — the post date, then the
 * business days after it. Five of them unless the caller says otherwise.
 *
 * Index i is video i+1, which is the same index `days` uses in a week plan, so a
 * slot and its date are always read with the same number.
 *
 * ⚠️ `count` IS THE PLAN'S LENGTH, NOT A CONFIGURATION. A week holds as many
 * videos as its stored plan has days, and that length was frozen from the
 * creator's commitment when the week was minted. Every caller with a plan in
 * its hand passes `days.length`; the default is for the legacy weeks that have
 * no plan at all, which are five and always were.
 */
export function publishDays(postDate: string, count: number = VIDEOS_PER_WEEK): string[] {
  if (!ISO.test(postDate)) return [];
  // A week of no days is a week with no grid — an empty array, never a crash.
  // `count` reaches here from a stored plan's length, which is data.
  if (count < 1) return [];

  const days = [postDate];
  let cursor = postDate;
  while (days.length < count) {
    do {
      cursor = addDaysUtc(cursor, 1);
    } while (isWeekend(cursor));
    days.push(cursor);
  }
  return days;
}

/** Day-of-week (Sun=0) of an ISO date, at UTC — getUTCDay's own numbering, no shift. */
export function dowOf(iso: string): number {
  const m = ISO.exec(iso);
  if (!m) return 0;
  const [, y, mo, d] = m;
  return new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d))).getUTCDay();
}

/** "2026-07-27" → "Jul 27". The Videos-page heading — short month, no ambiguity for a mixed roster. */
export function formatShort(iso: string): string {
  const m = ISO.exec(iso);
  if (!m) return iso;
  const [, , mo, d] = m;
  return `${MONTHS[Number(mo) - 1]} ${Number(d)}`;
}

/**
 * "2026-07-13" → "7/13". Numeric, because it rides INSIDE a status badge
 * ("Approved 7/13") where the words are already carrying the meaning and the
 * date is a footnote — "Approved Jul 13" is a third of the row's width.
 */
export function formatNumeric(iso: string): string {
  const m = ISO.exec(iso);
  if (!m) return iso;
  const [, , mo, d] = m;
  return `${Number(mo)}/${Number(d)}`;
}

/**
 * The viewer's OWN calendar date as `YYYY-MM-DD` — the one date in this app that
 * is LOCAL, not UTC. Everything else here is a stored
 * calendar date that must read the same in every timezone; this is a wall-clock
 * question about the person clicking. An AM in Los Angeles at 5pm Tuesday must
 * still count Wednesday as editable even though it is already Wednesday in UTC.
 */
export function todayLocalIso(now: Date = new Date()): string {
  const y = now.getFullYear();
  const mo = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${mo}-${d}`;
}

/**
 * Is a publish day FROZEN — strictly before today, and so a historical fact that
 * dragging would rewrite? Today itself is not frozen, nor is anything after
 * it. A dateless day (`""`) is never frozen: a week with no post date has no
 * calendar to have passed. A pure string compare on `YYYY-MM-DD`, which sorts
 * chronologically — no timezone conversion, because the question is only "what
 * does the viewer's own wall calendar say?".
 */
export function isDayFrozen(publishIso: string, todayIso: string): boolean {
  if (!ISO.test(publishIso)) return false;
  return publishIso < todayIso;
}

/** "2026-07-27" → "Mon Jul 27". The status-bar line — weekday abbr + short month. */
export function formatWithWeekday(iso: string): string {
  if (!ISO.test(iso)) return iso;
  return `${WEEKDAYS[dowOf(iso)]} ${formatShort(iso)}`;
}

/** A cell as its config-calendar label: {week:0,dow:1} → "Wk1-Tue". */
export function cellLabel(c: Cell): string {
  return `Wk${c.week + 1}-${WEEKDAYS[c.dow]}`;
}

/** A Sun=0 day-of-week as its abbreviation: 1 → "Mon". Names the constraint the post-date picker enforces. */
export function dowName(dow: number): string {
  return WEEKDAYS[dow];
}

const WEEKDAYS_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]; // Sun=0

/**
 * A Sun=0 day-of-week in full: 1 → "Monday". For prose, where the abbreviation
 * cannot go — "this brand posts on Mondays", never "on Mons".
 */
export function dowNameFull(dow: number): string {
  return WEEKDAYS_FULL[dow];
}

// ─── The post-date picker's calendar ─────────────────────────────────────────
// Kept here, pure, rather than inside the component: a calendar that is wrong at
// a month boundary is wrong in a way you only notice in December, and that is
// exactly the kind of thing a unit test should be catching instead of a user.

const MONTHS_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** "July 2026" — the picker's month heading. `month` is 1-12. */
export function monthLabel(year: number, month: number): string {
  return `${MONTHS_FULL[month - 1]} ${year}`;
}

/** Step a (year, month) pair by whole months, wrapping the year. `month` is 1-12. */
export function addMonths(year: number, month: number, delta: number): { year: number; month: number } {
  const zero = year * 12 + (month - 1) + delta;
  return { year: Math.floor(zero / 12), month: (zero % 12) + 1 };
}

/**
 * A month as SUN-FIRST calendar weeks: rows of 7, `null` where the cell falls
 * outside the month.
 *
 * Sun-first because the whole schedule is Sun=0 (a `Cell`'s dow, `dowOf`,
 * `DEFAULT_SCHEDULE`, the config grid) — a grid that started on Monday would put
 * the release column one place off from every other surface that draws this data,
 * and the bug would look like a styling problem.
 *
 * Padded with null rather than with the neighbouring month's days: a greyed-out
 * Jun 30 sitting in the July grid is a day you can neither pick nor reason about,
 * and the picker's job is to show which days are legal, not to fill a rectangle.
 */
export function monthGrid(year: number, month: number): (string | null)[][] {
  const firstDow = new Date(Date.UTC(year, month - 1, 1)).getUTCDay(); // Sun=0
  // Day 0 of the NEXT month is the last day of this one — the standard trick, and
  // it gets February right in a leap year without a table.
  const days = new Date(Date.UTC(year, month, 0)).getUTCDate();

  const p = (n: number) => String(n).padStart(2, "0");
  const cells: (string | null)[] = [
    ...Array<null>(firstDow).fill(null),
    ...Array.from({ length: days }, (_, i) => `${year}-${p(month)}-${p(i + 1)}`),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const rows: (string | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
  return rows;
}
